import { useMemo } from 'react';
import { HasLanguageProficiencyCodes } from '@ab/ams-school-api/types/private/v1';
import { Flex, GridAlpha, Text, Card } from '@applyboard/crystal-ui';
import { LabelValue } from '~/components/uie-fix/LabelValue';
import { formatNonTimezoneDate } from '~/utils';
import { LanguageProficiency } from '@ab/ams-school-api/src/resources/applications/languageProficiency/types';
import {
  CloseOutlineIcon,
  CheckMarkMultiOutlineIcon,
  WarningOutlineIcon,
  GlobeOutlineIcon,
} from '@applyboard/ui-icons';
import { DeepStringifyDates } from '@ab/ams-school-backend-utils';
import { DocumentReviewStatus } from '~/utils/languageProficiency';
interface LanguageProficiencyDataProps {
  isVerifyExamEnabledResult: boolean;
  isOtherTestTypes: boolean;
  documentReviewStatus: DocumentReviewStatus;
  languageProficiency: DeepStringifyDates<LanguageProficiency>;
}

export function LanguageProficiencyData({
  isVerifyExamEnabledResult,
  isOtherTestTypes,
  documentReviewStatus,
  languageProficiency,
}: LanguageProficiencyDataProps) {
  const isVerifyExamEnabled = isVerifyExamEnabledResult;
  const getDocumentReviewStatus = useMemo(() => {
    switch (documentReviewStatus) {
      case 'VERIFIED':
        return (
          <Flex direction="row" gap={1}>
            <CheckMarkMultiOutlineIcon color="positive" />
            <Text variant="labelL" contrast="high" intent="positive">
              Verified
            </Text>
          </Flex>
        );
      case 'UNABLE_TO_VERIFY':
        return (
          <Flex direction="row" gap={1}>
            <CloseOutlineIcon color="secondary.dark" />
            <Text variant="labelL" contrast="high" intent="secondary">
              Unable to verify
            </Text>
          </Flex>
        );
      case 'NO_RECORD_FOUND':
        return (
          <Flex direction="row" gap={1}>
            <CloseOutlineIcon color="negative" />
            <Text variant="labelL" contrast="high" intent="negative">
              No record found
            </Text>
          </Flex>
        );
      case 'IDENTITY_MISMATCH':
        return (
          <Flex direction="row" gap={1}>
            <WarningOutlineIcon color="warning" />
            <Text variant="labelL" contrast="high" intent="warning">
              Identity mismatch
            </Text>
          </Flex>
        );
      default:
        return null;
    }
  }, [documentReviewStatus]);

  return (
    <>
      {languageProficiency.status === HasLanguageProficiencyCodes.HAVE &&
        !isOtherTestTypes &&
        !languageProficiency.languageProficiencyData.isAttachedToApplication && (
          <Flex gap={6} direction="column">
            <Flex direction="row" gap={1} justify="between">
              <Flex direction="row" gap={1}>
                <LabelValue
                  label="Test type"
                  value={languageProficiency.languageProficiencyData.testType.title}
                  variant="titleS"
                  icon={<GlobeOutlineIcon />}
                />
              </Flex>
              {isVerifyExamEnabled && getDocumentReviewStatus}
            </Flex>

            <Flex gap={6} direction="column">
              {languageProficiency.languageProficiencyData.certificateNumber?.title &&
                languageProficiency.languageProficiencyData.certificateNumber?.value && (
                  <Flex.Item>
                    <LabelValue
                      label={languageProficiency.languageProficiencyData.certificateNumber?.title as string}
                      value={languageProficiency.languageProficiencyData.certificateNumber?.value as string}
                      gap={2}
                    />
                  </Flex.Item>
                )}
              <Flex.Item>
                {documentReviewStatus !== 'UNABLE_TO_VERIFY' && documentReviewStatus !== 'NO_RECORD_FOUND' && (
                  <Card variant="highlighted">
                    <GridAlpha columns="repeat(3, 1fr)">
                      {languageProficiency.languageProficiencyData.overallScore?.value && (
                        <LabelValue
                          label="Overall Score"
                          value={languageProficiency.languageProficiencyData.overallScore?.value as string}
                          variant="titleS"
                          gap={1}
                        />
                      )}
                      {languageProficiency.languageProficiencyData.testDate && (
                        <LabelValue
                          label="Test Date"
                          variant="titleS"
                          gap={1}
                          value={formatNonTimezoneDate(
                            new Date(languageProficiency.languageProficiencyData.testDate as string)
                          )}
                        />
                      )}
                      {languageProficiency.languageProficiencyData.source && (
                        <LabelValue
                          label="Source"
                          value={languageProficiency.languageProficiencyData.source as string}
                          variant="titleS"
                          gap={1}
                        />
                      )}
                    </GridAlpha>
                  </Card>
                )}
              </Flex.Item>
            </Flex>
            {languageProficiency.languageProficiencyData?.subscores &&
              documentReviewStatus !== 'UNABLE_TO_VERIFY' &&
              documentReviewStatus !== 'NO_RECORD_FOUND' && (
                <>
                  <LabelValue label="" value="Individual Band Subscores" variant="labelL" />
                  <GridAlpha rows="repeat(1, auto)" columnGap={2} rowGap={6} columns="repeat(5, 1fr)">
                    <>
                      {languageProficiency.languageProficiencyData.subscores?.map((s) => (
                        <LabelValue key={s.title} label={s.title} value={s.formattedValue} variant="titleS" />
                      ))}
                    </>
                  </GridAlpha>
                </>
              )}
            {languageProficiency.languageProficiencyData?.integratedSubscores &&
              documentReviewStatus !== 'UNABLE_TO_VERIFY' &&
              documentReviewStatus !== 'NO_RECORD_FOUND' && (
                <>
                  <LabelValue label="" value="Integrated Subscores" variant="titleS" />

                  <GridAlpha rows="repeat(1, auto)" columnGap={2} rowGap={6} columns="repeat(5, 1fr)">
                    <>
                      {languageProficiency.languageProficiencyData.integratedSubscores?.map((s) => (
                        <LabelValue key={s.title} label={s.title} value={s.formattedValue} variant="titleS" />
                      ))}
                    </>
                  </GridAlpha>
                </>
              )}
          </Flex>
        )}

      {languageProficiency.status === HasLanguageProficiencyCodes.HAVE &&
        !isOtherTestTypes &&
        languageProficiency.languageProficiencyData.isAttachedToApplication && (
          <Flex gap={6} direction="column">
            <Flex direction="row" gap={1} justify="between">
              <LabelValue
                label="Test type"
                value={languageProficiency.languageProficiencyData.testType.title}
                variant="titleS"
                icon={<GlobeOutlineIcon />}
              />
            </Flex>
            <Flex gap={6} direction="column">
              <Flex.Item>
                <LabelValue
                  label={languageProficiency.languageProficiencyData.certificateNumber?.title as string}
                  value={languageProficiency.languageProficiencyData.certificateNumber?.value as string}
                  gap={2}
                />
              </Flex.Item>
              <Flex.Item>
                <Card variant="highlighted">
                  <GridAlpha columns="repeat(2, 1fr)">
                    {languageProficiency.languageProficiencyData.overallScore?.value && (
                      <LabelValue
                        label="Overall Score"
                        value={languageProficiency.languageProficiencyData.overallScore?.value as string}
                        variant="titleS"
                        gap={1}
                      />
                    )}
                    <LabelValue
                      label="Test Date"
                      variant="titleS"
                      gap={1}
                      value={formatNonTimezoneDate(
                        new Date(languageProficiency.languageProficiencyData.testDate as string)
                      )}
                    />
                  </GridAlpha>
                </Card>
              </Flex.Item>
            </Flex>

            {languageProficiency.languageProficiencyData?.subscores && (
              <>
                <LabelValue label="" value="Individual Band Subscores" variant="labelL" />
                <GridAlpha rows="repeat(1, auto)" columnGap={2} rowGap={6} columns="repeat(5, 1fr)">
                  <>
                    {languageProficiency.languageProficiencyData.subscores?.map((s) => (
                      <LabelValue key={s.title} label={s.title} value={s.formattedValue} variant="titleS" />
                    ))}
                  </>
                </GridAlpha>
              </>
            )}

            {languageProficiency.languageProficiencyData?.integratedSubscores && (
              <>
                <LabelValue label="" value="Integrated Subscores" variant="titleS" />
                <GridAlpha rows="repeat(1, auto)" columnGap={2} rowGap={6} columns="repeat(5, 1fr)">
                  <>
                    {languageProficiency.languageProficiencyData.integratedSubscores?.map((s) => (
                      <LabelValue key={s.title} label={s.title} value={s.formattedValue} variant="titleS" />
                    ))}
                  </>
                </GridAlpha>
              </>
            )}
          </Flex>
        )}

      {languageProficiency.status === HasLanguageProficiencyCodes.HAVE && isOtherTestTypes && (
        <Flex gap={6} direction="column">
          <Flex gap={10}>
            <LabelValue
              label="Proof Type"
              value={languageProficiency.languageProficiencyData.testType.title}
              variant="titleS"
              icon={<GlobeOutlineIcon />}
            />
            {languageProficiency.languageProficiencyData.description?.title &&
              languageProficiency.languageProficiencyData.description?.value && (
                <Flex.Item>
                  <LabelValue
                    label={languageProficiency.languageProficiencyData.description?.title as string}
                    value={languageProficiency.languageProficiencyData.description?.value}
                  />
                </Flex.Item>
              )}
          </Flex>
        </Flex>
      )}
    </>
  );
}
